import React, { useEffect, useMemo, useState } from "react";
import { auth, db } from "../../lib/firebase";
import {
  collection,
  doc,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";

function safe(v) {
  return String(v ?? "").trim();
}

function toMillis(ts) {
  try {
    if (!ts) return 0;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.getTime();
  } catch {
    return 0;
  }
}

function timeAgo(ts) {
  try {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "now";
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    return `${days}d`;
  } catch {
    return "";
  }
}

function pillStyle(status) {
  const s = safe(status).toUpperCase();
  if (s === "OPEN") return S.pillOpen;
  if (s === "REVIEWING") return S.pillReview;
  if (s === "RESOLVED") return S.pillSolved;
  if (s === "REJECTED") return S.pillRejected;
  return S.pillDefault;
}

export default function AdminReportsScreen({ goBack, role }) {
  // ✅ FIX 1: normalize role
  const roleNorm = safe(role).toUpperCase();
  const hasAccess = roleNorm === "ADMIN" || roleNorm === "WORKER";

  // staff uid (who is acting)
  const staffUid = auth.currentUser?.uid || "";

  const [tab, setTab] = useState("OPEN"); // OPEN | REVIEWING | RESOLVED | REJECTED | ALL
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [err, setErr] = useState("");

  const [activeId, setActiveId] = useState("");
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");

  // ✅ list (NO orderBy here => avoids composite index)
  useEffect(() => {
    if (!hasAccess) return;

    setLoading(true);
    setErr("");

    const ref = collection(db, "reports");
    const qy =
      tab === "ALL"
        ? query(ref, limit(200))
        : query(ref, where("status", "==", tab), limit(200));

    const unsub = onSnapshot(
      qy,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // ✅ sort client-side by createdAt desc
        rows.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));

        setReports(rows);
        setLoading(false);

        if (activeId) {
          const stillHere = rows.some((r) => r.id === activeId);
          if (!stillHere) {
            setActiveId("");
            setActive(null);
          }
        }
      },
      (e) => {
        console.error(e);
        setErr(e?.message || "Failed to load reports");
        setLoading(false);
      }
    );

    return () => unsub();
  }, [hasAccess, tab, activeId]);

  // ✅ active report live
  useEffect(() => {
    if (!activeId) {
      setActive(null);
      return;
    }
    const unsub = onSnapshot(
      doc(db, "reports", activeId),
      (snap) => setActive(snap.exists() ? { id: snap.id, ...snap.data() } : null),
      (e) => console.error(e)
    );
    return () => unsub();
  }, [activeId]);

  const canAct = useMemo(() => !!activeId && !!active, [activeId, active]);

  const setStatus = async (status) => {
    if (!canAct) return;
    try {
      setErr("");

      await updateDoc(doc(db, "reports", activeId), {
        status,
        assignedTo: staffUid,
        resolvedBy: status === "RESOLVED" || status === "REJECTED" ? staffUid : "",
        resolutionNote: safe(note),
        updatedAt: serverTimestamp(),
      });

      if (status === "RESOLVED" || status === "REJECTED") {
        setActiveId("");
        setActive(null);
        setNote("");
      }
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Update failed");
    }
  };

  const banTargetUserIfPossible = async () => {
    if (!active || safe(active.type).toUpperCase() !== "USER") return;
    const targetId = safe(active.targetId);
    if (!targetId) return;

    try {
      setErr("");
      await updateDoc(doc(db, "users", targetId), {
        // ✅ keep consistent with your other admin screen field name
        isBanned: true,
        banReason: "Banned from report",
        bannedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Ban failed");
    }
  };

  if (!hasAccess) {
    return (
      <div style={S.page}>
        <div style={S.header}>
          <button style={S.backBtn} onClick={goBack} type="button">
            ←
          </button>
          <div>
            <div style={S.title}>Reports</div>
            <div style={S.subtitle}>Access denied</div>
          </div>
        </div>
        <div style={S.glassCard}>
          You don’t have access. <br />
          <span style={{ fontWeight: 900, fontSize: 12, color: "rgba(15,23,42,0.65)" }}>
            Your role: <b>{roleNorm || "—"}</b>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.header}>
        <button style={S.backBtn} onClick={goBack} type="button">
          ←
        </button>
        <div style={{ minWidth: 0 }}>
          <div style={S.title}>Reports</div>
          <div style={S.subtitle}>Review and resolve user reports</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          {/* ✅ FIX 2: show normalized role */}
          <span style={S.roleBadge}>{roleNorm}</span>
        </div>
      </div>

      <div style={S.container}>
        <div style={S.topBar}>
          <div style={S.tabs}>
            {["OPEN", "REVIEWING", "RESOLVED", "REJECTED", "ALL"].map((t) => (
              <button
                key={t}
                style={{ ...S.tab, ...(tab === t ? S.tabOn : null) }}
                onClick={() => setTab(t)}
                type="button"
              >
                {t}
              </button>
            ))}
          </div>

          {err ? <div style={S.toastErr}>{err}</div> : <div />}
        </div>

        <div style={S.split}>
          {/* LEFT */}
          <div style={S.left}>
            <div style={S.leftHeader}>
              <div style={S.leftTitle}>Queue</div>
              <div style={S.leftHint}>
                {loading ? "Loading…" : `${reports.length} report(s)`}
              </div>
            </div>

            <div style={S.list}>
              {loading ? (
                <div style={S.skel}>Loading reports…</div>
              ) : reports.length === 0 ? (
                <div style={S.skel}>No reports here 🎉</div>
              ) : (
                reports.map((r) => {
                  const isOn = r.id === activeId;
                  const status = safe(r.status) || "OPEN";
                  return (
                    <button
                      key={r.id}
                      style={{ ...S.row, ...(isOn ? S.rowOn : null) }}
                      onClick={() => {
                        setActiveId(r.id);
                        setNote(safe(r.resolutionNote));
                      }}
                      type="button"
                    >
                      <div style={S.rowTop}>
                        <div style={S.rowTitle}>
                          {safe(r.reason) || safe(r.type) || "Report"}
                        </div>
                        <span style={{ ...S.pill, ...pillStyle(status) }}>{safe(status).toUpperCase()}</span>
                      </div>

                      <div style={S.rowMid}>
                        <span style={S.chip}>Type: {safe(r.type) || "—"}</span>
                        <span style={S.chip}>Target: {safe(r.targetId) || "—"}</span>
                      </div>

                      <div style={S.rowBot}>
                        <span style={S.small}>{safe(r.email) || safe(r.uid) || "user"}</span>
                        <span style={S.small}>{timeAgo(r.createdAt)}</span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* RIGHT */}
          <div style={S.right}>
            {!active ? (
              <div style={S.emptyRight}>
                <div style={S.emptyIcon}>🧾</div>
                <div style={S.emptyTitle}>Select a report</div>
                <div style={S.emptySub}>Pick one from the left to review details.</div>
              </div>
            ) : (
              <div style={S.detail}>
                <div style={S.detailHeader}>
                  <div>
                    <div style={S.detailTitle}>Report Detail</div>
                    <div style={S.detailMeta}>
                      <span style={S.mono}>#{active.id.slice(0, 8)}</span>
                      <span style={S.dot}>•</span>
                      <span style={{ ...S.pill, ...pillStyle(active.status) }}>
                        {safe(active.status) || "OPEN"}
                      </span>
                    </div>
                  </div>
                </div>

                <div style={S.kv}>
                  <div style={S.k}>Type</div>
                  <div style={S.v}>{safe(active.type) || "—"}</div>
                </div>

                <div style={S.kv}>
                  <div style={S.k}>Reason</div>
                  <div style={S.v}>{safe(active.reason) || "—"}</div>
                </div>

                <div style={S.kv}>
                  <div style={S.k}>Target</div>
                  <div style={S.v}>{safe(active.targetId) || "—"}</div>
                </div>

                <div style={S.kv}>
                  <div style={S.k}>Reporter</div>
                  <div style={S.v}>{safe(active.email) || safe(active.uid) || "—"}</div>
                </div>

                {safe(active.details) ? (
                  <div style={S.box}>
                    <div style={S.boxTitle}>Details</div>
                    <div style={S.boxText}>{active.details}</div>
                  </div>
                ) : null}

                <div style={{ marginTop: 12 }}>
                  <div style={S.label}>Resolution note (optional)</div>
                  <textarea
                    style={S.textarea}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Write what you did…"
                  />
                </div>

                <div style={S.actions}>
                  <button style={S.actionBtn} onClick={() => setStatus("REVIEWING")} type="button">
                    Mark reviewing
                  </button>
                  <button
                    style={{ ...S.actionBtn, ...S.goodBtn }}
                    onClick={() => setStatus("RESOLVED")}
                    type="button"
                  >
                    Resolve
                  </button>
                  <button
                    style={{ ...S.actionBtn, ...S.badBtn }}
                    onClick={() => setStatus("REJECTED")}
                    type="button"
                  >
                    Reject
                  </button>
                  {safe(active.type).toUpperCase() === "USER" ? (
                    <button
                      style={{ ...S.actionBtn, ...S.darkBtn }}
                      onClick={banTargetUserIfPossible}
                      type="button"
                    >
                      Ban user
                    </button>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={S.footerNote}>No index needed here — sorting is done on the client.</div>
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: "100vh",
    padding: 14,
    background:
      "radial-gradient(900px 500px at 20% 10%, rgba(99,102,241,0.20), transparent 60%), radial-gradient(900px 500px at 80% 20%, rgba(236,72,153,0.16), transparent 60%), linear-gradient(180deg, #f8fafc 0%, #eef2ff 55%, #f8fafc 100%)",
    fontFamily:
      'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial',
    color: "#0f172a",
  },

  header: {
    maxWidth: 1200,
    margin: "0 auto 12px",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  backBtn: {
    width: 44,
    height: 44,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.85)",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
    cursor: "pointer",
    fontWeight: 900,
  },
  title: { fontWeight: 950, fontSize: 20, letterSpacing: -0.3 },
  subtitle: { marginTop: 2, fontWeight: 800, fontSize: 12, color: "rgba(15,23,42,0.55)" },
  roleBadge: {
    padding: "7px 10px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.80)",
    fontSize: 12,
    fontWeight: 950,
  },

  container: { maxWidth: 1200, margin: "0 auto" },

  topBar: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 12,
    alignItems: "center",
    marginBottom: 12,
  },

  tabs: { display: "flex", gap: 10, flexWrap: "wrap" },
  tab: {
    padding: "10px 12px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.75)",
    cursor: "pointer",
    fontWeight: 950,
    fontSize: 12,
  },
  tabOn: {
    background: "rgba(99,102,241,0.14)",
    border: "1px solid rgba(99,102,241,0.26)",
  },

  toastErr: {
    justifySelf: "end",
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(225,29,72,0.22)",
    background: "rgba(225,29,72,0.10)",
    fontWeight: 900,
    fontSize: 12,
    color: "rgba(136,19,55,0.95)",
  },

  split: { display: "grid", gridTemplateColumns: "410px 1fr", gap: 12, minHeight: "72vh" },

  left: {
    borderRadius: 20,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.72)",
    boxShadow: "0 14px 35px rgba(15, 23, 42, 0.06)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  leftHeader: {
    padding: 14,
    borderBottom: "1px solid rgba(15,23,42,0.08)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
  },
  leftTitle: { fontWeight: 950, fontSize: 14 },
  leftHint: { fontWeight: 850, fontSize: 12, color: "rgba(15,23,42,0.55)" },

  list: { padding: 12, overflowY: "auto", display: "grid", gap: 10, minHeight: 0 },

  skel: {
    padding: 14,
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.85)",
    fontWeight: 900,
    color: "rgba(15,23,42,0.65)",
  },

  row: {
    textAlign: "left",
    padding: 12,
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.88)",
    cursor: "pointer",
  },
  rowOn: {
    border: "1px solid rgba(99,102,241,0.28)",
    background: "rgba(99,102,241,0.10)",
  },
  rowTop: { display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between" },
  rowTitle: {
    fontWeight: 950,
    fontSize: 13,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 260,
  },
  rowMid: { marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" },
  rowBot: { marginTop: 10, display: "flex", justifyContent: "space-between", gap: 10 },

  chip: {
    fontSize: 11,
    fontWeight: 900,
    padding: "4px 8px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(2,6,23,0.02)",
    color: "rgba(15,23,42,0.75)",
  },
  small: { fontSize: 11, fontWeight: 900, color: "rgba(15,23,42,0.55)" },

  right: {
    borderRadius: 20,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.80)",
    boxShadow: "0 14px 35px rgba(15, 23, 42, 0.06)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },

  emptyRight: {
    height: "100%",
    padding: 18,
    display: "grid",
    placeItems: "center",
    textAlign: "center",
  },
  emptyIcon: { fontSize: 34 },
  emptyTitle: { fontWeight: 950, fontSize: 16, marginTop: 6 },
  emptySub: { fontWeight: 850, fontSize: 12, color: "rgba(15,23,42,0.55)", marginTop: 4 },

  detail: { padding: 14, overflowY: "auto" },
  detailHeader: { display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" },
  detailTitle: { fontWeight: 950, fontSize: 14 },
  detailMeta: { marginTop: 6, fontSize: 12, fontWeight: 850, color: "rgba(15,23,42,0.55)" },
  mono: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontWeight: 900 },
  dot: { margin: "0 6px", opacity: 0.6 },

  kv: {
    marginTop: 10,
    display: "grid",
    gridTemplateColumns: "120px 1fr",
    gap: 10,
    padding: "10px 0",
    borderBottom: "1px solid rgba(15,23,42,0.08)",
  },
  k: { fontSize: 12, fontWeight: 900, color: "rgba(15,23,42,0.55)" },
  v: { fontSize: 12, fontWeight: 900, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis" },

  box: {
    marginTop: 12,
    padding: 12,
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.92)",
  },
  boxTitle: { fontWeight: 950, fontSize: 12 },
  boxText: {
    marginTop: 8,
    fontSize: 12,
    fontWeight: 850,
    color: "rgba(15,23,42,0.70)",
    whiteSpace: "pre-wrap",
    lineHeight: 1.4,
  },

  label: { marginTop: 6, fontSize: 12, fontWeight: 900, color: "rgba(15,23,42,0.60)" },
  textarea: {
    width: "100%",
    minHeight: 110,
    padding: "12px 12px",
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.12)",
    outline: "none",
    fontWeight: 900,
    background: "rgba(255,255,255,0.95)",
    resize: "vertical",
  },

  actions: { marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" },
  actionBtn: {
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.9)",
    cursor: "pointer",
    fontWeight: 950,
    fontSize: 12,
  },
  goodBtn: { background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.22)" },
  badBtn: { background: "rgba(225,29,72,0.10)", border: "1px solid rgba(225,29,72,0.22)" },
  darkBtn: { background: "rgba(2,6,23,0.06)", border: "1px solid rgba(2,6,23,0.14)" },

  pill: {
    padding: "6px 9px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 950,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(248,250,252,0.9)",
  },
  pillOpen: { background: "rgba(236,72,153,0.10)", border: "1px solid rgba(236,72,153,0.20)" },
  pillReview: { background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.22)" },
  pillSolved: { background: "rgba(34,197,94,0.10)", border: "1px solid rgba(34,197,94,0.20)" },
  pillRejected: { background: "rgba(225,29,72,0.10)", border: "1px solid rgba(225,29,72,0.20)" },
  pillDefault: { background: "rgba(2,6,23,0.04)", border: "1px solid rgba(2,6,23,0.10)" },

  footerNote: { marginTop: 10, fontSize: 12, fontWeight: 850, color: "rgba(15,23,42,0.55)" },
  glassCard: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: 14,
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.85)",
    fontWeight: 900,
  },
};
